// @tennis-matches/services/bootstrap
// All steps are imperative only – no React, no hooks.

export { bootstrapWebApp } from "./webBootstrap.js";

import {
  installGlobalErrorOverlay,
  installBurgerMenuNavigation,
  installA11yFontSize,
  applyFontSize,
} from "@tennis-matches/core";
import { applyCourtColor } from "@tennis-matches/core/utils/courtColor";
import {
  applyCachedAppearanceTheme,
  applyAppearanceTheme,
  loadAppearanceThemeRemote,
} from "@tennis-matches/services/appearance";
import { loadUserSettings, DEFAULT_SETTINGS } from "@tennis-matches/services/settings";
import { initRemotePersistence } from "../persistence/index.js";
import {
  setRemoteMessages,
  initRemoteI18n,
  loadCachedI18nMessages,
} from "../i18n/index.js";

/** Default Supabase-ready poller when mode === 'schleifchen' and opts.waitForSupabaseConfig not provided. */
function defaultWaitForSupabaseConfig(maxWait = 2000) {
  return new Promise((resolve) => {
    const start = Date.now();
    function check() {
      if (typeof window !== "undefined" && window.__TTB_ENV__ && window.__TTB_ENV__.SUPABASE_URL) {
        resolve(true);
        return;
      }
      if (Date.now() - start >= maxWait) {
        resolve(false);
        return;
      }
      setTimeout(check, 50);
    }
    check();
  });
}

/**
 * Schedules fn to run after first paint (Quick Win #2/#3).
 * Keeps v_i18n / appearance / settings remote requests out of initial network.
 */
function runAfterFirstPaint(fn) {
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(fn, { timeout: 500 });
  } else {
    setTimeout(fn, 0);
  }
}

/**
 * Internal: install global UI behaviors (error overlay, burger menu, a11y font size).
 * Same try/catch pattern as previously in apps.
 *
 * @param {Object} opts
 * @param {(path: string) => void} [opts.navigateTo]
 * @param {() => unknown} [opts.getState]
 * @param {(fn: (s: unknown) => unknown) => void} [opts.setState]
 */
function installGlobalUI(opts) {
  const { navigateTo, getState, setState } = opts;
  try {
    installGlobalErrorOverlay();
  } catch (_) {}
  try {
    installBurgerMenuNavigation({ navigateTo });
  } catch (_) {}
  try {
    installA11yFontSize({ getState, setState });
  } catch (_) {}
}

/**
 * Internal: cached theme + font size + court color sync (initial apply + subscribe).
 * DEFAULT_SETTINGS from services/settings. Timing: cached first; remote theme/settings stay in app or webBootstrap.
 *
 * @param {Object} opts
 * @param {() => unknown} [opts.getState]
 * @param {(fn: (s: unknown) => unknown) => void} [opts.setState]
 * @param {(fn: (s: unknown) => void) => (() => void)|undefined} [opts.subscribe]
 */
function initVisualSync(opts) {
  const { getState, setState, subscribe } = opts;
  try {
    applyCachedAppearanceTheme();
  } catch (_) {}
  try {
    applyFontSize(getState());
  } catch (_) {}

  try {
    const s0 = getState();
    const color =
      (s0 && s0.shared && s0.shared.settings && s0.shared.settings.courtColor) ||
      DEFAULT_SETTINGS.courtColor;
    applyCourtColor(color);
  } catch (_) {}

  try {
    if (typeof subscribe === "function") {
      subscribe((s) => {
        try {
          applyFontSize(s);
        } catch (_) {}
        try {
          const color = s && s.shared && s.shared.settings && s.shared.settings.courtColor;
          if (color) applyCourtColor(color);
        } catch (_) {}
      });
    }
  } catch (_) {}
}

/**
 * Internal: remote persistence sync; settings + appearance remote DEFERRED after first paint (Quick Win #3).
 * applyCachedAppearanceTheme already ran in initVisualSync → no FOUC; remote overwrites later.
 *
 * @param {Object} opts
 * @param {() => unknown} [opts.getState]
 * @param {(fn: (s: unknown) => unknown) => void} [opts.setState]
 */
function initRemoteData(opts) {
  const { getState, setState } = opts;
  try {
    initRemotePersistence({ setState });
  } catch (_) {}

  runAfterFirstPaint(() => {
    Promise.resolve()
      .then(async () => {
        const res = await loadUserSettings();
        const settings = (res && res.settings) || DEFAULT_SETTINGS;
        setState((prev) => ({
          ...prev,
          shared: {
            ...(prev && prev.shared ? prev.shared : {}),
            settings,
          },
        }));
        try {
          applyCourtColor(
            (settings && settings.courtColor) || DEFAULT_SETTINGS.courtColor
          );
        } catch (_) {}
      })
      .catch(() => {});

    Promise.resolve()
      .then(async () => {
        const res = await loadAppearanceThemeRemote();
        if (res && res.ok && res.theme && typeof res.theme === "object") {
          applyAppearanceTheme(res.theme);
          setState((prev) => ({
            ...prev,
            shared: {
              ...(prev && prev.shared ? prev.shared : {}),
              appearanceTheme: res.theme,
            },
          }));
        }
      })
      .catch(() => {});
  });
}

/**
 * Internal: i18n boot – (A) sync cached preseed, (B) deferred remote init after first paint.
 * tt() works immediately from cache/defaults; remote messages update UI later.
 *
 * @param {Object} opts
 * @param {() => unknown} opts.getState
 * @param {(fn: (s: unknown) => unknown) => void} opts.setState
 * @param {() => void} [opts.onI18nUpdated]
 * @param {string} [opts.mode] 'web' | 'schleifchen'
 * @param {string} [opts.logPrefix]
 * @param {(maxWait?: number) => Promise<boolean>} [opts.waitForSupabaseConfig] only used when mode === 'schleifchen'
 * @returns {Promise<void>}
 */
async function bootstrapI18n(opts) {
  const mode = opts.mode === "schleifchen" ? "schleifchen" : "web";
  const waitForSupabaseConfig =
    opts.waitForSupabaseConfig ?? (mode === "schleifchen" ? defaultWaitForSupabaseConfig : undefined);
  const {
    getState,
    setState,
    onI18nUpdated,
    logPrefix = mode === "schleifchen" ? "[i18n]" : "[web]",
  } = opts;

  function requestRender() {
    try {
      setState((s) => ({ ...s }));
      if (typeof onI18nUpdated === "function") onI18nUpdated();
    } catch (_) {}
  }

  // A) Initial (sync): only cache – tt() works immediately, no remote in initial load
  const defaultLocale = mode === "schleifchen" ? "de" : "de-DE";
  const raw = getState()?.app?.locale ?? defaultLocale;
  const bootLocale = String(raw).trim() || defaultLocale;
  try {
    const cached = loadCachedI18nMessages(bootLocale);
    if (
      cached &&
      typeof cached === "object" &&
      Object.keys(cached).length > 0
    ) {
      setRemoteMessages(cached);
      if (mode === "web") {
        console.log(logPrefix, "Loaded cached i18n messages:", Object.keys(cached).length, "keys");
      } else {
        console.log(logPrefix, "Pre-seeded", Object.keys(cached).length, "messages from cache");
      }
    } else {
      if (mode === "web") {
        console.warn(logPrefix, "No cached i18n messages found for locale:", bootLocale);
      }
    }
  } catch (e) {
    console.warn(logPrefix, "Cache pre-seed failed:", e);
  }

  // B) Deferred (after first paint): initRemoteI18n / v_i18n_keys_messages – no initial request
  function startRemoteI18n() {
    initRemoteI18n({ getState, setRemoteMessages, requestRender })
      .then(() => {
        console.log(logPrefix, "Remote i18n initialization completed");
      })
      .catch((e) => {
        console.error(logPrefix, "Remote i18n initialization failed:", e);
      });
  }

  if (mode === "web") {
    runAfterFirstPaint(() => {
      try {
        console.log(logPrefix, "Initializing remote i18n (deferred)...");
        startRemoteI18n();
      } catch (e) {
        console.error(logPrefix, "Failed to start deferred remote i18n:", e);
      }
    });
    return;
  }

  if (mode === "schleifchen") {
    runAfterFirstPaint(() => {
      (async () => {
        if (typeof waitForSupabaseConfig === "function") {
          try {
            const ready = await waitForSupabaseConfig(2000);
            if (!ready) {
              console.warn(logPrefix, "Proceeding without Supabase config - using fallback");
            }
          } catch (_) {}
        }
        try {
          console.log(logPrefix, "Initializing remote i18n (deferred)...");
          await initRemoteI18n({ getState, setRemoteMessages, requestRender });
          console.log(logPrefix, "Remote i18n initialization completed");
        } catch (e) {
          console.error(logPrefix, "Remote i18n initialization failed:", e);
        }
      })();
    });
    return;
  }
}

/**
 * Shared app bootstrap – i18n: sync cached preseed, deferred remote (after first paint).
 * requestRender is built internally from setState + onI18nUpdated.
 *
 * @param {Object} opts
 * @param {string} [opts.mode] 'web' | 'schleifchen'
 * @param {() => unknown} [opts.getState]
 * @param {(fn: (s: unknown) => unknown) => void} [opts.setState]
 * @param {(fn: (s: unknown) => void) => (() => void)|undefined} [opts.subscribe]
 * @param {(path: string) => void} [opts.navigateTo]
 * @param {() => void} [opts.onI18nUpdated] e.g. () => window.dispatchEvent(new CustomEvent('i18n-updated'))
 * @param {string} [opts.logPrefix]
 * @param {(maxWait?: number) => Promise<boolean>} [opts.waitForSupabaseConfig] only for mode 'schleifchen'
 * @returns {Promise<{ ok: boolean }>}
 */
export async function bootstrapApp(opts) {
  await bootstrapI18n(opts);
  installGlobalUI({
    navigateTo: opts.navigateTo,
    getState: opts.getState,
    setState: opts.setState,
  });
  initVisualSync({
    getState: opts.getState,
    setState: opts.setState,
    subscribe: opts.subscribe,
  });
  initRemoteData({
    getState: opts.getState,
    setState: opts.setState,
  });
  return { ok: true };
}
